import { useEffect, useRef, useState } from "react";
import {
  buildPreviewConstraints,
  canContinueReadiness,
  resolveSelectedDevice,
  toDeviceOptions,
  type DeviceOption,
  type MediaDevicePreferences
} from "../../services/mediaDevicePreferences";
import { evaluateMediaSupport, MediaUnavailableError, mediaSupportCode } from "../../services/mediaSupport";
import { useI18n } from "../../i18n/localization";
import { MaterialMediaIcon } from "./MaterialMediaIcon";

// MiroTalk-style "get ready" screen shown BEFORE the traffic waiting station / field officer
// directory (design/05). Live camera preview + camera/mic toggles + device selectors, then a
// Hebrew action button. On continue the chosen devices + initial on/off state are handed back
// (MediaDevicePreferences) and carried into the call. Preview tracks are always stopped on
// unmount / continue so the camera light never leaks.
function toReadinessError(e: unknown, noDevice: string, messageFor: (code: string) => string): string {
  if (e instanceof MediaUnavailableError) return messageFor(mediaSupportCode(e.support));
  if (e instanceof DOMException) {
    if (e.name === "NotAllowedError" || e.name === "SecurityError") return messageFor("VC-MEDIA-001");
    if (e.name === "NotFoundError" || e.name === "OverconstrainedError") return noDevice;
    return messageFor("VC-SYS-001");
  }
  return messageFor("VC-SYS-001");
}

export function DeviceReadiness({
  role,
  onReady
}: {
  role: "traffic" | "field";
  onReady: (prefs: MediaDevicePreferences) => void;
}) {
  const { t, dir, messageFor } = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameras, setCameras] = useState<DeviceOption[]>([]);
  const [mics, setMics] = useState<DeviceOption[]>([]);
  const [cameraId, setCameraId] = useState<string | undefined>(undefined);
  const [micId, setMicId] = useState<string | undefined>(undefined);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // True only after a preview stream has been acquired; gates the continue button so the
  // user cannot proceed before camera/mic access is proven.
  const [streamReady, setStreamReady] = useState(false);
  const [retry, setRetry] = useState(0);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  // Acquire (or re-acquire) the preview whenever the selected device changes or on retry.
  // Request permission first, THEN enumerate so device labels are populated (browsers hide
  // labels until a grant). Toggles do not re-acquire — they flip track.enabled directly.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setStreamReady(false); // re-prove access on every (re)acquire before continue is allowed
    const support = evaluateMediaSupport();
    if (support !== "ok") {
      setError(messageFor(mediaSupportCode(support)));
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(
          buildPreviewConstraints({ cameraDeviceId: cameraId, micDeviceId: micId })
        );
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        stopStream();
        streamRef.current = stream;
        const vt = stream.getVideoTracks()[0];
        if (vt) vt.enabled = cameraEnabled;
        const at = stream.getAudioTracks()[0];
        if (at) at.enabled = micEnabled;
        if (videoRef.current) videoRef.current.srcObject = stream;

        const devices = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        const cams = toDeviceOptions(devices, "videoinput", (i) => `${t.readiness.camera} ${i + 1}`);
        const ms = toDeviceOptions(devices, "audioinput", (i) => `${t.readiness.microphone} ${i + 1}`);
        setCameras(cams);
        setMics(ms);
        // Default the selection to a concrete device on first load (one extra refresh).
        const nextCam = resolveSelectedDevice(cameraId, cams);
        const nextMic = resolveSelectedDevice(micId, ms);
        if (nextCam !== cameraId) setCameraId(nextCam);
        if (nextMic !== micId) setMicId(nextMic);
        setError(null);
        setStreamReady(true);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(toReadinessError(e, t.readiness.noDevice, messageFor));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      stopStream();
    };
    // cameraEnabled/micEnabled are intentionally excluded: toggling must not re-acquire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, micId, retry]);

  const toggleCamera = () => {
    const next = !cameraEnabled;
    setCameraEnabled(next);
    const vt = streamRef.current?.getVideoTracks()[0];
    if (vt) vt.enabled = next;
  };
  const toggleMic = () => {
    const next = !micEnabled;
    setMicEnabled(next);
    const at = streamRef.current?.getAudioTracks()[0];
    if (at) at.enabled = next;
  };

  const canContinue = canContinueReadiness({ loading, error: !!error, streamReady });

  const onContinue = () => {
    if (!canContinue) return; // guard: never proceed before device access is proven
    stopStream();
    onReady({ cameraDeviceId: cameraId, micDeviceId: micId, cameraEnabled, micEnabled });
  };

  const continueLabel = role === "traffic" ? t.readiness.continueTraffic : t.readiness.continueField;

  return (
    <main
      dir={dir}
      style={{
        height: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
        gap: 12, padding: 16, maxWidth: 720, margin: "0 auto", width: "100%", boxSizing: "border-box",
        fontFamily: "Segoe UI, Arial, sans-serif"
      }}
    >
      <div style={{ flex: "0 0 auto" }}>
        <h1 style={{ color: "#14305f", fontSize: 18, margin: "0 0 2px" }}>{t.readiness.title}</h1>
        <p style={{ color: "#5b6b82", fontSize: 13, margin: 0 }}>{t.readiness.subtitle}</p>
      </div>

      {/* Preview fills the remaining height and clips; the controls + button below stay in view. */}
      <div style={{ flex: 1, minHeight: 0, position: "relative", background: "#0f1e3a", borderRadius: 10, overflow: "hidden" }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)", opacity: cameraEnabled ? 1 : 0 }}
        />
        {!cameraEnabled && !error && (
          <div style={overlayCentre}>{t.readiness.cameraOffPreview}</div>
        )}
        {error && (
          <div style={{ ...overlayCentre, flexDirection: "column", gap: 12, padding: 20, textAlign: "center" }}>
            <span style={{ maxWidth: 420 }}>{error}</span>
            <button onClick={() => { setError(null); setRetry((r) => r + 1); }} style={primaryBtn}>
              {t.readiness.retry}
            </button>
          </div>
        )}
        {loading && !error && <div style={{ ...overlayCentre, fontSize: 13 }}>{t.readiness.requesting}</div>}
      </div>

      {/* Device selectors + on/off toggles. Wrap on narrow screens. */}
      <div style={{ flex: "0 0 auto", display: "flex", flexWrap: "wrap", gap: 10 }}>
        <label style={field}>
          <span style={fieldLabel}>{t.readiness.camera}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <select value={cameraId ?? ""} onChange={(e) => setCameraId(e.target.value || undefined)} style={select} disabled={!!error}>
              {cameras.length === 0 && <option value="">—</option>}
              {cameras.map((c) => <option key={c.deviceId} value={c.deviceId}>{c.label}</option>)}
            </select>
            <button
              type="button"
              onClick={toggleCamera}
              style={toggleBtn(cameraEnabled)}
              aria-label={cameraEnabled ? t.readiness.cameraOn : t.readiness.cameraOff}
              aria-pressed={cameraEnabled}
              title={cameraEnabled ? t.readiness.cameraOn : t.readiness.cameraOff}
            >
              <MaterialMediaIcon name={cameraEnabled ? "video_camera_front" : "video_camera_front_off"} />
            </button>
          </div>
        </label>

        <label style={field}>
          <span style={fieldLabel}>{t.readiness.microphone}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <select value={micId ?? ""} onChange={(e) => setMicId(e.target.value || undefined)} style={select} disabled={!!error}>
              {mics.length === 0 && <option value="">—</option>}
              {mics.map((m) => <option key={m.deviceId} value={m.deviceId}>{m.label}</option>)}
            </select>
            <button
              type="button"
              onClick={toggleMic}
              style={toggleBtn(micEnabled)}
              aria-label={micEnabled ? t.readiness.micOn : t.readiness.micOff}
              aria-pressed={micEnabled}
              title={micEnabled ? t.readiness.micOn : t.readiness.micOff}
            >
              <MaterialMediaIcon name={micEnabled ? "mic" : "mic_off"} />
            </button>
          </div>
        </label>
      </div>

      <button
        onClick={onContinue}
        disabled={!canContinue}
        style={{ ...primaryBtn, flex: "0 0 auto", opacity: canContinue ? 1 : 0.5, cursor: canContinue ? "pointer" : "not-allowed" }}
      >
        {loading && !error ? t.readiness.requesting : continueLabel}
      </button>
    </main>
  );
}

const overlayCentre: React.CSSProperties = {
  position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
  color: "#dbe7ff", fontSize: 15, background: "rgba(15,30,58,0.35)"
};
const field: React.CSSProperties = { flex: "1 1 260px", minWidth: 220, display: "flex", flexDirection: "column", gap: 4 };
const fieldLabel: React.CSSProperties = { fontSize: 12, color: "#64748b" };
const select: React.CSSProperties = { flex: 1, minWidth: 0, padding: "8px 10px", border: "1px solid #d6deea", borderRadius: 6, fontSize: 14 };
const primaryBtn: React.CSSProperties = {
  background: "#15803d", color: "#fff", border: "none", borderRadius: 8, padding: "12px 22px", fontSize: 15, cursor: "pointer"
};
function toggleBtn(on: boolean): React.CSSProperties {
  return {
    background: on ? "#1d4ed8" : "#e2e8f0", color: on ? "#fff" : "#1e2a3a",
    border: "none", borderRadius: 6, width: 44, minWidth: 44, height: 38, padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, cursor: "pointer", whiteSpace: "nowrap"
  };
}
